/**
 * `decodeStored` (decision 0036, spec §2.7–2.8): the ONE entry point per stored or wire shape, used at every decode
 * site. Input is the parsed JSON form (`Schema.toCodecJson`, what the DOs store and send). Output is always the current
 * type.
 *
 * - Versioned records: events (`EventMeta.v`: 1 = v1 payload) and snapshots (`ProjectState.v`: absent = v1) are
 *   decoded with the schema their marker names, v1 then upcast.
 * - Unversioned records: decoded with the v2 schema STRICTLY first (`onExcessProperty: "error"`, so a v1 record whose
 *   differences are renamed optional keys can't pass as v2 and silently lose them); if that fails, with the frozen v1
 *   schema, then transformed; if that fails too, `StoredDecodeError` (which never carries the payload).
 */
import { Schema } from "effect"
import { Command, CommandEnvelope, CommandResult } from "../commands/index"
import { WakeBatch, WakeReason } from "../domain/agentlife"
import { Comment, MemoryItem } from "../domain/knowledge"
import { Proposal } from "../domain/proposal"
import { ProjectEventEnvelope } from "../events"
import { AgentInbound, ProjectInbound, RequestTurnPayload, RunInbound, WorkspaceInbound } from "../internal"
import { ContextPack } from "../job"
import * as V1 from "../legacy/v1/index"
import { Run } from "../run"
import { ProjectState } from "../state"
import { BodyToMind } from "../stream"
import { fromV1Command, fromV1CommandEnvelope, fromV1CommandResult, V1CommandNotMapped } from "./commands"
import { fromV1EventEnvelope } from "./events"
import {
  fromV1Comment,
  fromV1ContextPack,
  fromV1MemoryItem,
  fromV1Run,
  fromV1WakeBatch,
  fromV1WakeReason,
} from "./records"
import { fromV1State } from "./state"
import {
  fromV1AgentInbound,
  fromV1BodyToMind,
  fromV1ProjectInbound,
  fromV1RequestTurnPayload,
  fromV1RunInbound,
  fromV1WorkspaceInbound,
} from "./wire"

/** A stored record neither decoder reads. Names the kind, its tag and seq, and both decoders' messages; never the payload. */
export class StoredDecodeError extends Error {
  readonly _tag = "StoredDecodeError"
  constructor(
    readonly kind: string,
    readonly tag: string | null,
    readonly seq: number | undefined,
    /** The v2 decoder's message (≤ 300 characters). */
    readonly v2: string,
    /** The v1 decoder's (or upcaster's) message (≤ 300 characters); "" when v1 was not tried. */
    readonly v1: string,
  ) {
    super(
      `cannot decode a stored ${kind}${tag ? ` ${tag}` : ""}${seq !== undefined ? ` at seq ${seq}` : ""}: v2: ${v2}${v1 ? `; v1: ${v1}` : ""}`,
    )
  }
}

/** A decoded record and whether it was upcast from v1 (the decode check counts those). */
export interface Decoded<A> {
  readonly value: A
  readonly upcast: boolean
}

// biome-ignore lint/suspicious/noExplicitAny: the JSON codec of any service-free schema
type AnyCodec = Schema.Codec<any, any, never, never>
type Decoder = (input: unknown) => unknown

const MAX = 300
/** A schema error's message, bounded. Effect does not report input values by default (`reportInput` off). */
const messageOf = (e: unknown): string => {
  const m = e instanceof Error ? e.message : String(e)
  return m.length > MAX ? `${m.slice(0, MAX - 1)}…` : m
}

const decoder = (schema: Schema.Top, strict: boolean): Decoder =>
  Schema.decodeUnknownSync(
    Schema.toCodecJson(schema as never) as unknown as AnyCodec,
    strict ? { onExcessProperty: "error" } : undefined,
  ) as Decoder

/** Lazily built decoders (module load stays cheap). */
const lazy = (schema: () => Schema.Top, strict = false): Decoder => {
  let d: Decoder | null = null
  return (input) => {
    d ??= decoder(schema(), strict)
    return d(input)
  }
}

const tagOf = (json: unknown): string | null => {
  if (json === null || typeof json !== "object") return null
  const o = json as Record<string, unknown>
  for (const k of ["_tag", "type", "kind"]) if (typeof o[k] === "string") return o[k] as string
  const nested = o.command ?? o.event
  if (nested !== null && typeof nested === "object") {
    const t = (nested as Record<string, unknown>)._tag
    if (typeof t === "string") return t
  }
  return null
}

const seqOf = (json: unknown): number | undefined => {
  if (json === null || typeof json !== "object") return undefined
  const s = (json as Record<string, unknown>).seq
  return typeof s === "number" ? s : undefined
}

/**
 * v2 only, strictly: records only new code writes (wave K's `decided` projection holds `Proposal`s encoded from the
 * current state). No v1 fallback; a failure is a StoredDecodeError with an empty v1 message.
 */
const v2Only = <A>(kind: string, v2: Decoder): ((json: unknown) => Decoded<A>) => {
  return (json) => {
    try {
      return { value: v2(json) as A, upcast: false }
    } catch (e) {
      throw new StoredDecodeError(kind, tagOf(json), seqOf(json), messageOf(e), "")
    }
  }
}

/** Strict v2, else v1 + transform, else StoredDecodeError. */
const unversioned = <A, V>(
  kind: string,
  v2: Decoder,
  v1: Decoder,
  transform: (v: V) => A,
): ((json: unknown) => Decoded<A>) => {
  return (json) => {
    let v2Message: string
    try {
      return { value: v2(json) as A, upcast: false }
    } catch (e) {
      v2Message = messageOf(e)
    }
    try {
      return { value: transform(v1(json) as V), upcast: true }
    } catch (e) {
      throw new StoredDecodeError(kind, tagOf(json), seqOf(json), v2Message, messageOf(e))
    }
  }
}

const D = {
  event: lazy(() => ProjectEventEnvelope),
  eventV1: lazy(() => V1.ProjectEventEnvelope),
  state: lazy(() => ProjectState),
  stateV1: lazy(() => V1.ProjectState),
  run: lazy(() => Run, true),
  runV1: lazy(() => V1.Run),
  comment: lazy(() => Comment, true),
  commentV1: lazy(() => V1.Comment),
  commandResult: lazy(() => CommandResult, true),
  commandResultV1: lazy(() => V1.CommandResult),
  command: lazy(() => Command, true),
  commandV1: lazy(() => V1.Command),
  commandEnvelope: lazy(() => CommandEnvelope, true),
  commandEnvelopeV1: lazy(() => V1.CommandEnvelope),
  projectInbound: lazy(() => ProjectInbound, true),
  projectInboundV1: lazy(() => V1.ProjectInbound),
  workspaceInbound: lazy(() => WorkspaceInbound, true),
  workspaceInboundV1: lazy(() => V1.WorkspaceInbound),
  agentInbound: lazy(() => AgentInbound, true),
  agentInboundV1: lazy(() => V1.AgentInbound),
  runInbound: lazy(() => RunInbound, true),
  runInboundV1: lazy(() => V1.RunInbound),
  wakeReason: lazy(() => WakeReason, true),
  wakeReasonV1: lazy(() => V1.WakeReason),
  wakeBatch: lazy(() => WakeBatch, true),
  wakeBatchV1: lazy(() => V1.WakeBatch),
  contextPack: lazy(() => ContextPack, true),
  contextPackV1: lazy(() => V1.ContextPack),
  bodyToMind: lazy(() => BodyToMind, true),
  bodyToMindV1: lazy(() => V1.BodyToMind),
  requestTurn: lazy(() => RequestTurnPayload, true),
  requestTurnV1: lazy(() => Schema.Struct({ runId: V1.RunId, agentId: V1.AgentId, wakeBatch: V1.WakeBatch })),
  memoryItem: lazy(() => MemoryItem, true),
  memoryItemV1: lazy(() => V1.MemoryItem),
  proposal: lazy(() => Proposal, true),
}

/** `V1CommandNotMapped` (the intake commands) surfaces as a decode failure with its "reload Minga" message. */
const commandTransform =
  <V, A>(f: (v: V) => A) =>
  (v: V): A => {
    try {
      return f(v)
    } catch (e) {
      if (e instanceof V1CommandNotMapped) throw new Error(e.message)
      throw e
    }
  }

const eventEnvelope = (json: unknown): Decoded<ProjectEventEnvelope> => {
  const v = json !== null && typeof json === "object" ? (json as { readonly v?: unknown }).v : undefined
  if (v === 1) {
    try {
      return { value: fromV1EventEnvelope(D.eventV1(json) as V1.ProjectEventEnvelope), upcast: true }
    } catch (e) {
      throw new StoredDecodeError("eventEnvelope", tagOf(json), seqOf(json), "", messageOf(e))
    }
  }
  try {
    return { value: D.event(json) as ProjectEventEnvelope, upcast: false }
  } catch (e) {
    throw new StoredDecodeError("eventEnvelope", tagOf(json), seqOf(json), messageOf(e), "")
  }
}

const projectState = (json: unknown): Decoded<ProjectState> => {
  const versioned = json !== null && typeof json === "object" && "v" in json
  if (versioned) {
    try {
      return { value: D.state(json) as ProjectState, upcast: false }
    } catch (e) {
      throw new StoredDecodeError("projectState", null, seqOf(json), messageOf(e), "")
    }
  }
  try {
    return { value: fromV1State(D.stateV1(json) as V1.ProjectState), upcast: true }
  } catch (e) {
    throw new StoredDecodeError("projectState", null, seqOf(json), "", messageOf(e))
  }
}

/** Every stored or wire shape with its decoder that also says whether the record was upcast (for the decode check). */
export const decodeStoredDetailed = {
  eventEnvelope,
  projectState,
  run: unversioned<Run, V1.Run>("run", D.run, D.runV1, fromV1Run),
  comment: unversioned<Comment, typeof V1.Comment.Type>("comment", D.comment, D.commentV1, fromV1Comment),
  commandResult: unversioned<CommandResult, V1.CommandResult>(
    "commandResult",
    D.commandResult,
    D.commandResultV1,
    fromV1CommandResult,
  ),
  command: unversioned<Command, V1.Command>(
    "command",
    D.command,
    D.commandV1,
    commandTransform(fromV1Command),
  ),
  commandEnvelope: unversioned<CommandEnvelope, typeof V1.CommandEnvelope.Type>(
    "commandEnvelope",
    D.commandEnvelope,
    D.commandEnvelopeV1,
    commandTransform(fromV1CommandEnvelope),
  ),
  projectInbound: unversioned<ProjectInbound, typeof V1.ProjectInbound.Type>(
    "projectInbound",
    D.projectInbound,
    D.projectInboundV1,
    commandTransform(fromV1ProjectInbound),
  ),
  workspaceInbound: unversioned<WorkspaceInbound, typeof V1.WorkspaceInbound.Type>(
    "workspaceInbound",
    D.workspaceInbound,
    D.workspaceInboundV1,
    fromV1WorkspaceInbound,
  ),
  agentInbound: unversioned<AgentInbound, typeof V1.AgentInbound.Type>(
    "agentInbound",
    D.agentInbound,
    D.agentInboundV1,
    fromV1AgentInbound,
  ),
  runInbound: unversioned<RunInbound, typeof V1.RunInbound.Type>(
    "runInbound",
    D.runInbound,
    D.runInboundV1,
    fromV1RunInbound,
  ),
  wakeReason: unversioned<WakeReason, typeof V1.WakeReason.Type>(
    "wakeReason",
    D.wakeReason,
    D.wakeReasonV1,
    fromV1WakeReason,
  ),
  wakeBatch: unversioned<WakeBatch, typeof V1.WakeBatch.Type>(
    "wakeBatch",
    D.wakeBatch,
    D.wakeBatchV1,
    fromV1WakeBatch,
  ),
  contextPack: unversioned<typeof ContextPack.Type, typeof V1.ContextPack.Type>(
    "contextPack",
    D.contextPack,
    D.contextPackV1,
    fromV1ContextPack,
  ),
  bodyToMind: unversioned<typeof BodyToMind.Type, typeof V1.BodyToMind.Type>(
    "bodyToMind",
    D.bodyToMind,
    D.bodyToMindV1,
    fromV1BodyToMind,
  ),
  requestTurnPayload: unversioned<RequestTurnPayload, Parameters<typeof fromV1RequestTurnPayload>[0]>(
    "requestTurnPayload",
    D.requestTurn,
    D.requestTurnV1,
    fromV1RequestTurnPayload,
  ),
  memoryItem: unversioned<MemoryItem, V1.MemoryItem>(
    "memoryItem",
    D.memoryItem,
    D.memoryItemV1,
    fromV1MemoryItem,
  ),
  /** Wave K: a row of the Project DO's `decided` projection (v2 only). */
  proposal: v2Only<Proposal>("proposal", D.proposal),
} as const

const valueOnly =
  <A>(f: (json: unknown) => Decoded<A>) =>
  (json: unknown): A =>
    f(json).value

/**
 * The AgentBody's meta `retry_batch` (`{batch: Wire<WakeBatch>, runId, attempts, at}`, a cloud shape): its `batch`
 * goes through `wakeBatch`; the other fields are returned as stored.
 */
const retryBatch = <T extends { readonly batch: unknown }>(
  json: T,
): Omit<T, "batch"> & { readonly batch: WakeBatch } => {
  const { batch, ...rest } = json
  return { ...rest, batch: decodeStoredDetailed.wakeBatch(batch).value }
}

/** One entry point per stored or wire shape (spec §2.8). Every one throws `StoredDecodeError` on a record it can't read. */
export const decodeStored = {
  eventEnvelope: valueOnly(decodeStoredDetailed.eventEnvelope),
  projectState: valueOnly(decodeStoredDetailed.projectState),
  run: valueOnly(decodeStoredDetailed.run),
  comment: valueOnly(decodeStoredDetailed.comment),
  commandResult: valueOnly(decodeStoredDetailed.commandResult),
  command: valueOnly(decodeStoredDetailed.command),
  commandEnvelope: valueOnly(decodeStoredDetailed.commandEnvelope),
  projectInbound: valueOnly(decodeStoredDetailed.projectInbound),
  workspaceInbound: valueOnly(decodeStoredDetailed.workspaceInbound),
  agentInbound: valueOnly(decodeStoredDetailed.agentInbound),
  runInbound: valueOnly(decodeStoredDetailed.runInbound),
  wakeReason: valueOnly(decodeStoredDetailed.wakeReason),
  wakeBatch: valueOnly(decodeStoredDetailed.wakeBatch),
  retryBatch,
  contextPack: valueOnly(decodeStoredDetailed.contextPack),
  bodyToMind: valueOnly(decodeStoredDetailed.bodyToMind),
  requestTurnPayload: valueOnly(decodeStoredDetailed.requestTurnPayload),
  memoryItem: valueOnly(decodeStoredDetailed.memoryItem),
  proposal: valueOnly(decodeStoredDetailed.proposal),
} as const
